import { BaseFieldJsonData, RenderOptions, FieldJsonData, getFieldGroup } from '../../models/element-types';
import { RendererBase } from '../renderer-base';
import { HtmlUtils } from '../../utils/html.utils';

export abstract class BaseFieldRenderer<TFieldJsonData extends FieldJsonData = FieldJsonData> extends RendererBase<TFieldJsonData, RenderOptions> {
    protected renderElement(fieldJsonData: FieldJsonData, options: RenderOptions = {}): HTMLElement {
        const wrapper = document.createElement('div');
        wrapper.id = fieldJsonData.id;
        wrapper.dataset['element'] = 'field';

        const normalizedType = fieldJsonData.type.toLowerCase();
        wrapper.dataset['field'] = normalizedType;

        const group = getFieldGroup(normalizedType as typeof fieldJsonData.type);
        if (group) {
            wrapper.dataset['group'] = group;
        }

        if (fieldJsonData.isSystem) {
            wrapper.dataset['system'] = 'true';
        }

        this.applyCustomCssClasses(wrapper, fieldJsonData.cssClasses, options);

        const elementContent = this.renderContent(fieldJsonData, options);
        wrapper.appendChild(elementContent);

        return wrapper;
    }

    protected renderContent(fieldJsonData: FieldJsonData, options: RenderOptions = {}): HTMLElement {
        const elementContent = document.createElement('div');
        elementContent.classList.add('element-content');

        const fieldContainer = document.createElement('div');
        fieldContainer.classList.add('field');
        fieldContainer.classList.toggle('required', fieldJsonData.required === true);

        if (!fieldJsonData.hiddenLabel) {
            const label = this.renderLabel(fieldJsonData);
            fieldContainer.appendChild(label);
        }

        const input = this.renderInput(fieldJsonData, options);
        fieldContainer.appendChild(input);

        const errorContainer = this.renderErrorContainer(fieldJsonData);
        fieldContainer.appendChild(errorContainer);

        if (fieldJsonData.showDescription) {
            const description = this.renderDescription(fieldJsonData);
            fieldContainer.appendChild(description);
        }

        elementContent.appendChild(fieldContainer);

        return elementContent;
    }

    public rerender(element: HTMLElement, fieldJsonData: TFieldJsonData, options: RenderOptions = {}): void {
        const oldContent = element.querySelector('.element-content');
        if (oldContent) {
            oldContent.remove();
        }

        const newContent = this.renderContent(fieldJsonData, options);
        element.appendChild(newContent);

        this.updateData(element, fieldJsonData);
    }

    protected override applyVisualProperties(element: HTMLElement, data: TFieldJsonData): void {
        const oldContent = element.querySelector('.element-content');
        if (oldContent) {
            oldContent.remove();
        }

        const newContent = this.renderContent(data);
        element.appendChild(newContent);
    }

    protected applyCustomCssClasses(element: HTMLElement, cssClasses?: string[], options: RenderOptions = {}): void {
        const isEditor = options.runtimeContext?.mode?.toLocaleLowerCase() === 'editor';
        if (isEditor) {
            return; // we don't add custom classes in the constructor because it may affect the constructor elements.
        }

        if (cssClasses && cssClasses.length > 0) {
            element.classList.add(...cssClasses);
        }
    }

    protected renderLabel(field: BaseFieldJsonData): HTMLElement {
        const label = document.createElement('label');
        label.classList.add('label');
        label.setAttribute('for', `field-${field.id}`);
        this.applyHtmlLabel(label, field.label);

        return label;
    }

    protected applyHtmlLabel(element: HTMLElement, label?: string): void {
        element.innerHTML = HtmlUtils.sanitize(label || '');
    }

    protected getPlainLabel(label?: string): string {
        const container = document.createElement('div');
        container.innerHTML = HtmlUtils.sanitize(label || '');

        return container.textContent?.trim() || '';
    }

    protected renderDescription(fieldJsonData: BaseFieldJsonData): HTMLElement {
        const description = document.createElement('span');
        description.classList.add('description');
        description.id = `description-${fieldJsonData.id}`;
        description.textContent = fieldJsonData.description || '';

        return description;
    }

    protected renderErrorContainer(fieldJsonData: BaseFieldJsonData): HTMLElement {
        const errorContainer = document.createElement('div');
        errorContainer.classList.add('fieldJsonData-errors');
        errorContainer.id = `errors-${fieldJsonData.id}`;
        errorContainer.setAttribute('role', 'alert');
        errorContainer.setAttribute('aria-live', 'assertive');

        return errorContainer;
    }

    protected applyCommonAttributes(input: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement, fieldJsonData: BaseFieldJsonData): void {
        input.id = `field-${fieldJsonData.id}`;
        input.name = fieldJsonData.id;

        if (fieldJsonData.required) {
            input.required = true;
        }
        if (!(input instanceof HTMLSelectElement)) {
            if (fieldJsonData.showPlaceholder && fieldJsonData.placeholder) {
                input.placeholder = fieldJsonData.placeholder;
            }
        }

        const plainLabel = this.getPlainLabel(fieldJsonData.label);
        if (plainLabel) {
            input.dataset['label'] = plainLabel;
        }

        input.setAttribute('aria-required', fieldJsonData.required ? 'true' : 'false');
        input.setAttribute('aria-invalid', 'false');
        if (fieldJsonData.description) {
            input.setAttribute('aria-describedby', `description-${fieldJsonData.id}`);
        }
    }

    protected abstract renderInput(fieldJsonData: BaseFieldJsonData, options: RenderOptions): HTMLElement;
}
